const MANAGED_ATTR = 'data-managed-seo';
const JSON_LD_TYPE = 'application/ld+json';

/**
 * Reuse the head node matching `selector`, or create a managed one.
 *
 * Reuse is mandatory: prerendered documents already ship these nodes, and a
 * second JSON-LD block with the same `@id` makes Google report duplicate
 * properties.
 */
function ensureHeadNode(selector, tagName, attributes) {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement(tagName);
    Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value));
    document.head.appendChild(node);
  }
  node.setAttribute(MANAGED_ATTR, '');
  return node;
}

/**
 * Description, Open Graph and Twitter tags for one public page.
 *
 * Optional: `ogLocale`, and `ogImage` (absolute URL) with `ogImageWidth`,
 * `ogImageHeight` and `ogImageAlt`. Pages without them get the same tags as before.
 *
 * @param {{ title: string, description: string, canonical: string }} meta
 * @returns {{ attr: 'name'|'property', key: string, content: string }[]}
 */
export function buildPageTags(meta, { ogType = 'website', card = 'summary_large_image' } = {}) {
  return [
    ['name', 'description', meta.description],
    ['property', 'og:title', meta.title],
    ['property', 'og:description', meta.description],
    ['property', 'og:type', ogType],
    ['property', 'og:url', meta.canonical],
    ['property', 'og:locale', meta.ogLocale],
    ['property', 'og:image', meta.ogImage],
    ['property', 'og:image:width', meta.ogImage && meta.ogImageWidth],
    ['property', 'og:image:height', meta.ogImage && meta.ogImageHeight],
    ['property', 'og:image:alt', meta.ogImage && meta.ogImageAlt],
    ['name', 'twitter:card', card],
    ['name', 'twitter:title', meta.title],
    ['name', 'twitter:description', meta.description],
    ['name', 'twitter:image', meta.ogImage],
  ]
    .filter(([, , content]) => content)
    .map(([attr, key, content]) => ({ attr, key, content: String(content) }));
}

/**
 * Write one page's SEO onto the live document — the CRA bundle's half of the
 * two-layer contract whose other half is the build-time prerender.
 *
 * `lang`/`dir` go on <html> (the shell ships `lang="fr"`), and `alternates`
 * replace any hreflang links, including prerendered ones, for this page.
 *
 * @param {{ title: string, canonical?: string, jsonLd?: Object[], lang?: string, dir?: 'ltr'|'rtl',
 *   alternates?: { hreflang: string, href: string }[] }} meta
 * @param {{ attr: 'name'|'property', key: string, content: string }[]} tags
 * @returns {() => void} cleanup restoring the previous title, lang and dir, and removing injected nodes
 */
export default function applyDocumentSeo(meta, tags) {
  const previousTitle = document.title;
  const root = document.documentElement;
  const previousRootAttributes = ['lang', 'dir'].map((name) => [name, root.getAttribute(name)]);
  document.title = meta.title;
  if (meta.lang) root.setAttribute('lang', meta.lang);
  if (meta.dir) root.setAttribute('dir', meta.dir);
  if (!tags.some(({ attr, key }) => attr === 'name' && key === 'robots')) {
    document.head.querySelectorAll('meta[name="robots"]').forEach((node) => node.remove());
  }
  if (!meta.jsonLd?.length) {
    document.head.querySelectorAll(`script[type="${JSON_LD_TYPE}"]`).forEach((node) => node.remove());
  }

  if (meta.alternates?.length) {
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((node) => node.remove());
    meta.alternates.forEach(({ hreflang, href }) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', href);
      link.setAttribute(MANAGED_ATTR, '');
      document.head.appendChild(link);
    });
  }

  tags.forEach(({ attr, key, content }) => {
    ensureHeadNode(`meta[${attr}="${key}"]`, 'meta', { [attr]: key }).setAttribute('content', content);
  });

  if (meta.canonical) {
    ensureHeadNode('link[rel="canonical"]', 'link', { rel: 'canonical' }).setAttribute('href', meta.canonical);
  }
  if (meta.jsonLd?.length) {
    ensureHeadNode(`script[type="${JSON_LD_TYPE}"]`, 'script', { type: JSON_LD_TYPE })
      .textContent = JSON.stringify(meta.jsonLd);
  }

  return () => {
    document.title = previousTitle;
    previousRootAttributes.forEach(([name, value]) => {
      if (value === null) root.removeAttribute(name);
      else root.setAttribute(name, value);
    });
    document.head.querySelectorAll(`[${MANAGED_ATTR}]`).forEach((node) => node.remove());
  };
}
